/**
 * Gün Sonu Raporu - TypeScript Arayüzleri
 * 
 * Bu dosya gün sonu raporu için gerekli tüm tip tanımlarını içerir.
 * Restoran ve Hızlı Satış için ayrı arayüzler tanımlanmıştır.
 */

// ==================== ORTAK TİPLER ====================

/** Rapor türü */
export type ReportType = 'RESTAURANT' | 'QUICK_SALE' | 'KIOSK' | 'QR_MENU' | 'PACKAGE_ORDER';

/** Fiş/Adisyon durumu */
export type TransactionStatus = 'PAID' | 'UNPAID' | 'CANCELLED' | 'PARTIAL';

/** Ödeme türü */
export type PaymentType = 'CASH' | 'POS' | 'MIXED' | 'OPEN_ACCOUNT' | 'ONLINE' | 'OTHER' | 'PREPAID' | 'NONE';

/** Filtre parametreleri */
export interface EndOfDayFilters {
  reportType: ReportType;
  startDate: Date;
  endDate: Date;
  screenId?: number | null;     // Ekran filtresi (opsiyonel)
  screenName?: string;          // Ekran adı (gösterim için)
}

/** Özet kartları için veri yapısı */
export interface EndOfDaySummary {
  totalAmount: number;          // Toplam tutar
  totalCount: number;           // Toplam işlem sayısı
  cashAmount: number;           // Nakit toplam
  cashCount: number;            // Nakit işlem sayısı
  posAmount: number;            // POS toplam
  posCount: number;             // POS işlem sayısı
  otherAmount: number;          // Diğer ödeme yöntemleri toplam (yemek kartı, hediye kartı vb.)
  otherCount: number;           // Diğer ödeme yöntemleri sayısı
  cancelledAmount: number;      // İptal toplam
  cancelledCount: number;       // İptal sayısı
  mixedAmount: number;          // Karma ödeme toplam
  mixedCount: number;           // Karma ödeme sayısı
  openAccountAmount: number;    // Açık hesap toplam
  openAccountCount: number;     // Açık hesap sayısı
  posRefundAmount: number;      // POS iade toplam (negatif)
  posRefundCount: number;       // POS iade sayısı
  prepaidAmount: number;        // Ön ödemeli (kart) satış toplam
  prepaidCount: number;         // Ön ödemeli satış sayısı
}

// ==================== RESTORAN TİPLERİ ====================

/** Restoran satır verisi (19 sütun - Şube ve Ekran dahil) */
export interface EndOfDayRestaurantRow {
  id: string;                   // Benzersiz ID
  rowNumber: number;            // 1. Sıra
  tableName: string;            // 2. Masa Adı
  orderNumber: string;          // 3. Adisyon No
  status: TransactionStatus;    // 4. Fiş Durumu
  subtotal: number;             // 5. Ara Tutar (KDV hariç)
  grandTotal: number;           // 6. Genel Toplam (KDV dahil)
  paidAmount: number;           // 7. Ödenen
  paymentType: PaymentType;     // 8. İşlem Türü
  cancelledTotal: number;       // 9. İptal Toplamı
  openedAt: Date | string;      // 10. Açılış Tarihi
  closedAt: Date | string;      // 11. Kapanış Tarihi (YENİ)
  seatingDuration: number;      // 12. Oturma Süresi (dakika)
  notes: string;                // 13. Açıklama
  receiptNumber: string;        // 14. Yazıcı Fiş No
  customerName: string;         // 15. Cari
  waiterName: string;           // 16. Garson
  invoiceNumber: string;        // 17. Fatura No
  branchName?: string;          // 18. Şube Adı
  screenName?: string;          // 19. Ekran Adı
  invoiceId?: string;           // ✅ YENİ: Fatura ID (ürün satış özeti için)
  isFiscalReceipt?: boolean;    // ÖKC üzerinden mi kesildi?
}

// ==================== HIZLI SATIŞ TİPLERİ ====================

/** Hızlı Satış satır verisi (17 sütun) */
export interface EndOfDayQuickSaleRow {
  id: string;                   // Benzersiz ID
  rowNumber: number;            // 1. Sıra
  referenceNumber: string;      // 2. Fiş No (HS-YYYYMM-XXXXXX)
  status: TransactionStatus;    // 3. Fiş Durumu
  subtotal: number;             // 4. Ara Tutar (KDV hariç)
  grandTotal: number;           // 5. Genel Toplam (KDV dahil)
  paidAmount: number;           // 6. Ödenen
  paymentType: PaymentType;     // 7. İşlem Türü
  paymentAccountName: string;   // 8. Kasa/Banka Adı (YENİ)
  cancelledTotal: number;       // 9. İptal Toplamı
  transactionDate: Date | string; // 10. İşlem Tarihi (Ödeme tarihi - receipt.CreatedAt)
  openedAt?: Date | string;      // 10b. Sipariş Tarihi (receipt.OpenedAt - platform siparişlerinde orijinal sipariş tarihi)
  notes: string;                // 11. Açıklama
  receiptNumber: string;        // 12. Yazıcı Fiş No
  customerName: string;         // 13. Cari
  invoiceNumber: string;        // 14. Fatura No
  branchName?: string;          // 15. Şube Adı
  /** QuickSaleReceipt.BranchId — şube seçili raporda satır filtresi için */
  branchId?: string | null;
  /** QuickSaleReceipt.ScreenId — hızlı satış ekranı filtresi için */
  screenId?: number | null;
  createdByName?: string;       // 16. Personel Adı
  cashierName?: string;         // 17. Kasiyer Adı
  paymentProviderName?: string; // 18. Platform (Yemek Sepeti, Getir vb.) ✅ YENİ
  // ✅ Özet hesaplaması için ödeme detayları (tabloda gösterilmez)
  payments?: Array<{
    paymentMethod: string;
    amount: number;
    cashboxName?: string | null;
    bankAccountName?: string | null;
    bankSubAccountName?: string | null; // Alt hesap adı
    paymentProviderName?: string | null; // ✅ YENİ: Platform adı
    paymentProviderType?: string | null; // ✅ YENİ: Detaylı ödeme türü (ONLINE_YEMEK SEPETİ_NAKİT vb.)
  }>;
  invoiceId?: string;           // ✅ YENİ: Fatura ID (ürün satış özeti için)
  isFiscalReceipt?: boolean;    // ÖKC üzerinden mi kesildi?
  isRefund?: boolean;           // POS iade kaydı mı?
  discount?: number;            // ✅ YENİ: Fişe uygulanan indirim tutarı (QuickSaleReceipt.DiscountAmount)
}

// ==================== KIOSK TİPLERİ ====================

/** Kiosk satır verisi */
export interface EndOfDayKioskRow {
  id: string;                   // Benzersiz ID
  rowNumber: number;            // 1. Sıra
  referenceNumber: string;      // 2. Sipariş No
  status: TransactionStatus;    // 3. Durum
  subtotal: number;             // 4. Ara Tutar (KDV hariç)
  grandTotal: number;           // 5. Genel Toplam (KDV dahil)
  paidAmount: number;           // 6. Ödenen
  paymentType: PaymentType;     // 7. Ödeme Türü
  cancelledTotal: number;       // 8. İptal Toplamı
  transactionDate: Date | string; // 9. İşlem Tarihi
  notes: string;                // 10. Açıklama
  customerName: string;         // 11. Müşteri
  kioskName: string;            // 12. Kiosk Adı
  invoiceId?: string;           // Fatura ID (ürün satış özeti için)
  invoiceNumber?: string;       // Fatura No (sağlama için)
  paymentAccountName?: string;  // ✅ Kasa/Banka Adı (özet hesaplama için)
  // ✅ Özet hesaplaması için ödeme detayları
  payments?: Array<{
    paymentMethod: string;
    amount: number;
    cashboxName?: string | null;
    bankAccountName?: string | null;
    bankSubAccountName?: string | null;
  }>;
}

// ==================== QR MENÜ TİPLERİ ====================

/** QR Menü satır verisi */
export interface EndOfDayQRMenuRow {
  id: string;                   // Benzersiz ID
  rowNumber: number;            // 1. Sıra
  orderNumber: string;          // 2. Sipariş No
  tableName: string;            // 3. Masa Adı
  status: TransactionStatus;    // 4. Durum
  subtotal: number;             // 5. Ara Tutar (KDV hariç)
  grandTotal: number;           // 6. Genel Toplam (KDV dahil)
  paidAmount: number;           // 7. Ödenen
  paymentType: PaymentType;     // 8. Ödeme Türü
  cancelledTotal: number;       // 9. İptal Toplamı
  transactionDate: Date | string; // 10. İşlem Tarihi
  notes: string;                // 11. Açıklama
  customerName: string;         // 12. Müşteri
  qrMenuName: string;           // 13. QR Menü Adı
  invoiceId?: string;           // Fatura ID (ürün satış özeti için)
  invoiceNumber?: string;       // Fatura No (sağlama için)
}

// ==================== EKRAN SEÇİMİ ====================

/** Ekran seçimi için dropdown öğesi */
export interface ScreenOption {
  id: number;
  name: string;
  type: ReportType;
  branchId?: string | null;
}

// ==================== API RESPONSE ====================

/** API yanıt yapısı */
export interface EndOfDayReportResponse<T> {
  success: boolean;
  message?: string;
  data: {
    summary: EndOfDaySummary;
    rows: T[];
  };
}

// ==================== YETKİ SİSTEMİ ====================

/** Gün sonu raporu yetkileri */
export interface EndOfDayPermissions {
  canViewReport: boolean;           // Raporu görüntüleyebilir mi?
  canViewTotals: boolean;           // Toplamları görebilir mi? (Özet kartları)
  canExportExcel: boolean;          // Excel'e aktarabilir mi?
  canViewRestaurant: boolean;       // Restoran raporunu görebilir mi?
  canViewQuickSale: boolean;        // Hızlı satış raporunu görebilir mi?
  canViewKiosk: boolean;            // Kiosk raporunu görebilir mi?
  canViewQRMenu: boolean;           // QR Menü raporunu görebilir mi?
}

// ==================== TABLO SÜTUN TANIMLARI ====================

/** Tablo sütun tanımı */
export interface TableColumnDef {
  id: string;
  label: string;
  field: string;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  format?: 'text' | 'currency' | 'date' | 'datetime' | 'duration' | 'status' | 'paymentType';
  visible?: boolean;
}

/** Restoran tablo sütunları */
export const RESTAURANT_COLUMNS: TableColumnDef[] = [
  { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', align: 'center', width: 60 },
  { id: 'tableName', label: 'Masa Adı', field: 'tableName', align: 'left', width: 100 },
  { id: 'orderNumber', label: 'Adisyon No', field: 'orderNumber', align: 'left', width: 120 },
  { id: 'status', label: 'Fiş Durumu', field: 'status', align: 'center', width: 100, format: 'status' },
  { id: 'subtotal', label: 'Ara Tutar', field: 'subtotal', align: 'right', width: 100, format: 'currency' },
  { id: 'grandTotal', label: 'Genel Toplam', field: 'grandTotal', align: 'right', width: 110, format: 'currency' },
  { id: 'paidAmount', label: 'Ödenen', field: 'paidAmount', align: 'right', width: 100, format: 'currency' },
  { id: 'paymentType', label: 'İşlem Türü', field: 'paymentType', align: 'center', width: 100, format: 'paymentType' },
  { id: 'cancelledTotal', label: 'İptal Toplamı', field: 'cancelledTotal', align: 'right', width: 100, format: 'currency' },
  { id: 'openedAt', label: 'Açılış Tarihi', field: 'openedAt', align: 'center', width: 140, format: 'datetime' },
  { id: 'closedAt', label: 'Kapanış Tarihi', field: 'closedAt', align: 'center', width: 140, format: 'datetime' },
  { id: 'seatingDuration', label: 'Oturma Süresi', field: 'seatingDuration', align: 'center', width: 100, format: 'duration' },
  { id: 'waiterName', label: 'Garson', field: 'waiterName', align: 'left', width: 100 },
  { id: 'notes', label: 'Açıklama', field: 'notes', align: 'left', width: 150 },
  { id: 'receiptNumber', label: 'Yazıcı Fiş No', field: 'receiptNumber', align: 'center', width: 100 },
  { id: 'customerName', label: 'Cari', field: 'customerName', align: 'left', width: 150 },
  { id: 'invoiceNumber', label: 'Fatura No', field: 'invoiceNumber', align: 'left', width: 130 },
  { id: 'branchName', label: 'Şube', field: 'branchName', align: 'left', width: 120 },
  { id: 'screenName', label: 'Ekran', field: 'screenName', align: 'left', width: 120 },
];

/** Hızlı Satış tablo sütunları */
export const QUICK_SALE_COLUMNS: TableColumnDef[] = [
  { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', align: 'center', width: 60 },
  { id: 'referenceNumber', label: 'Fiş No', field: 'referenceNumber', align: 'left', width: 160 },
  { id: 'status', label: 'Fiş Durumu', field: 'status', align: 'center', width: 100, format: 'status' },
  { id: 'grandTotal', label: 'Toplam', field: 'grandTotal', align: 'right', width: 100, format: 'currency' },
  { id: 'discount', label: 'İndirim', field: 'discount', align: 'right', width: 90, format: 'currency' },
  { id: 'paidAmount', label: 'Ödenen', field: 'paidAmount', align: 'right', width: 100, format: 'currency' },
  { id: 'paymentType', label: 'Ödeme Türü', field: 'paymentType', align: 'center', width: 100, format: 'paymentType' },
  { id: 'paymentAccountName', label: 'Kasa/Banka', field: 'paymentAccountName', align: 'left', width: 150 },
  { id: 'paymentProviderName', label: 'Platform', field: 'paymentProviderName', align: 'left', width: 130 }, // ✅ YENİ: Yemek Sepeti, Getir vb.
  { id: 'transactionDate', label: 'İşlem Tarihi', field: 'transactionDate', align: 'center', width: 140, format: 'datetime' },
  { id: 'customerName', label: 'Cari', field: 'customerName', align: 'left', width: 130 },
  { id: 'invoiceNumber', label: 'Fatura No', field: 'invoiceNumber', align: 'left', width: 140 },
  { id: 'branchName', label: 'Şube', field: 'branchName', align: 'left', width: 120 },
  { id: 'createdByName', label: 'Personel/Kasiyer', field: 'createdByName', align: 'left', width: 140 },
];

/** Kiosk tablo sütunları */
export const KIOSK_COLUMNS: TableColumnDef[] = [
  { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', align: 'center', width: 60 },
  { id: 'referenceNumber', label: 'Sipariş No', field: 'referenceNumber', align: 'left', width: 140 },
  { id: 'kioskName', label: 'Kiosk', field: 'kioskName', align: 'left', width: 120 },
  { id: 'status', label: 'Durum', field: 'status', align: 'center', width: 100, format: 'status' },
  { id: 'subtotal', label: 'Ara Tutar', field: 'subtotal', align: 'right', width: 100, format: 'currency' },
  { id: 'grandTotal', label: 'Genel Toplam', field: 'grandTotal', align: 'right', width: 110, format: 'currency' },
  { id: 'paidAmount', label: 'Ödenen', field: 'paidAmount', align: 'right', width: 100, format: 'currency' },
  { id: 'paymentType', label: 'Ödeme Türü', field: 'paymentType', align: 'center', width: 100, format: 'paymentType' },
  { id: 'cancelledTotal', label: 'İptal Toplamı', field: 'cancelledTotal', align: 'right', width: 100, format: 'currency' },
  { id: 'transactionDate', label: 'İşlem Tarihi', field: 'transactionDate', align: 'center', width: 140, format: 'datetime' },
  { id: 'customerName', label: 'Müşteri', field: 'customerName', align: 'left', width: 150 },
  { id: 'notes', label: 'Açıklama', field: 'notes', align: 'left', width: 150 },
];

/** QR Menü tablo sütunları */
export const QR_MENU_COLUMNS: TableColumnDef[] = [
  { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', align: 'center', width: 60 },
  { id: 'orderNumber', label: 'Sipariş No', field: 'orderNumber', align: 'left', width: 140 },
  { id: 'tableName', label: 'Masa', field: 'tableName', align: 'left', width: 100 },
  { id: 'qrMenuName', label: 'QR Menü', field: 'qrMenuName', align: 'left', width: 120 },
  { id: 'status', label: 'Durum', field: 'status', align: 'center', width: 100, format: 'status' },
  { id: 'subtotal', label: 'Ara Tutar', field: 'subtotal', align: 'right', width: 100, format: 'currency' },
  { id: 'grandTotal', label: 'Genel Toplam', field: 'grandTotal', align: 'right', width: 110, format: 'currency' },
  { id: 'paidAmount', label: 'Ödenen', field: 'paidAmount', align: 'right', width: 100, format: 'currency' },
  { id: 'paymentType', label: 'Ödeme Türü', field: 'paymentType', align: 'center', width: 100, format: 'paymentType' },
  { id: 'cancelledTotal', label: 'İptal Toplamı', field: 'cancelledTotal', align: 'right', width: 100, format: 'currency' },
  { id: 'transactionDate', label: 'İşlem Tarihi', field: 'transactionDate', align: 'center', width: 140, format: 'datetime' },
  { id: 'customerName', label: 'Müşteri', field: 'customerName', align: 'left', width: 150 },
  { id: 'notes', label: 'Açıklama', field: 'notes', align: 'left', width: 150 },
];

// ==================== PAKET SİPARİŞ TİPLERİ ====================

/** Paket Sipariş satır verisi */
export interface EndOfDayPackageOrderRow {
  id: string;                   // Benzersiz ID
  rowNumber: number;            // 1. Sıra
  orderNumber: string;          // 2. Sipariş No
  shortCode: string;            // 3. Kısa Kod
  status: TransactionStatus;    // 4. Sipariş Durumu
  orderStatus: string;          // 5. Teslimat Durumu (PENDING, READY, ONWAY, DELIVERED, CANCELLED)
  subtotal: number;             // 6. Ara Tutar (KDV hariç)
  grandTotal: number;           // 7. Genel Toplam (KDV dahil)
  paidAmount: number;           // 8. Ödenen
  paymentType: PaymentType;     // 9. Ödeme Türü
  paymentAccountName: string;   // 10. Kasa/Banka Adı
  cancelledTotal: number;       // 11. İptal Toplamı
  createdAt: Date | string;     // 12. Sipariş Tarihi
  paymentDate: Date | string | null; // 13. Ödeme Tarihi
  customerName: string;         // 14. Müşteri
  customerPhone: string;        // 15. Telefon
  customerAddress: string;      // 16. Adres
  courierName: string;          // 17. Kurye
  invoiceNumber: string;        // 18. Fatura No
  branchName?: string;          // 19. Şube Adı
  branchId?: string;            // Şube filtresi / ödenmemiş satırlar için (PlatformOrderSummary.branchId)
  cashierName?: string;         // 20. Kasiyer
  invoiceId?: string;           // Fatura ID (ürün satış özeti için)
  paymentProviderName?: string;
  // Ödeme detayları (özet hesaplaması için)
  cashAmount?: number;
  posAmount?: number;
  otherAmount?: number;

  // Yemeksepeti v2026 (15.05.2026) — sponsorluk dökümü altyapısı.
  // Backend (PaySalesService) henüz bu alanları doldurmuyor; UI yine yok.
  // Sadece tip imzası hazır; ileride backend YemeksepetiOrders.OrderDiscountsJson
  // okuyup buraya yazınca raporlama tek dokunusla aktive olur.
  ysPlatformDiscountAmount?: number;   // Yemeksepeti'nin (PLATFORM) karsiladigi indirim toplami
  ysVendorDiscountAmount?: number;     // Restoran (VENDOR) karsiladigi indirim toplami
  ysThirdPartyDiscountAmount?: number; // 3. taraf (THIRD_PARTY) karsiladigi indirim toplami
  ysTotalDiscountAmount?: number;      // Tum indirimlerin toplami (orderDiscounts.amount toplami)

  // Platform sipariş ID'leri — EndOfDayReport'ta satıra tıklandığında platform-spesifik
  // detay modallarını açmak için kullanılır. QuickSaleReceipt rowları kendi başına
  // platformun entity ID'sini taşımaz; reconciliation aşamasında platform
  // listesinden invoiceId/invoiceNumber/orderNumber eşleşmesiyle propagate edilir.
  getirOrderId?: number;
  migrosOrderId?: number;
  yemeksepetiOrderId?: number;
  trendyolOrderId?: number;
  /** PackageOrders.Id — Paket (telefon) sipariş detay modali için. PKT- prefixli
   *  fişlerde reconciliation aşamasında platform listesinden eşleşmeyle dolar.
   *  Cancel akışındaki satırlarda mapping anında direkt set edilir. */
  packageOrderId?: number;

  /** İndirim öncesi sepet toplamı (modal'larda gösterilen "İndirimsiz/Ara Toplam"). */
  originalTotal?: number;
}

/** Paket Sipariş tablo sütunları */
export const PACKAGE_ORDER_COLUMNS: TableColumnDef[] = [
  { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', align: 'center', width: 60 },
  { id: 'orderNumber', label: 'Sipariş No', field: 'orderNumber', align: 'left', width: 120 },
  { id: 'shortCode', label: 'Kısa Kod', field: 'shortCode', align: 'center', width: 80 },
  { id: 'status', label: 'Ödeme Durumu', field: 'status', align: 'center', width: 100, format: 'status' },
  { id: 'orderStatus', label: 'Teslimat', field: 'orderStatus', align: 'center', width: 100 },
  { id: 'grandTotal', label: 'Toplam', field: 'grandTotal', align: 'right', width: 100, format: 'currency' },
  { id: 'originalTotal', label: 'İndirimsiz Toplam', field: 'originalTotal', align: 'right', width: 130, format: 'currency' },
  { id: 'paidAmount', label: 'Ödenen', field: 'paidAmount', align: 'right', width: 100, format: 'currency' },
  { id: 'paymentType', label: 'Ödeme Türü', field: 'paymentType', align: 'center', width: 100, format: 'paymentType' },
  { id: 'paymentAccountName', label: 'Kasa/Banka', field: 'paymentAccountName', align: 'left', width: 130 },
  { id: 'createdAt', label: 'Sipariş Tarihi', field: 'createdAt', align: 'center', width: 140, format: 'datetime' },
  { id: 'paymentDate', label: 'Ödeme Tarihi', field: 'paymentDate', align: 'center', width: 140, format: 'datetime' },
  { id: 'customerName', label: 'Müşteri', field: 'customerName', align: 'left', width: 130 },
  { id: 'customerPhone', label: 'Telefon', field: 'customerPhone', align: 'left', width: 110 },
  { id: 'courierName', label: 'Kurye', field: 'courierName', align: 'left', width: 100 },
  { id: 'invoiceNumber', label: 'Fatura No', field: 'invoiceNumber', align: 'left', width: 130 },
  { id: 'branchName', label: 'Şube', field: 'branchName', align: 'left', width: 100 },
  { id: 'cashierName', label: 'Kasiyer', field: 'cashierName', align: 'left', width: 100 },
];

// ==================== SAGLAMA (RECONCILIATION) TİPLERİ ====================

/** Platform sipariş özeti - tüm platformlardan gelen ham sipariş verisi için ortak yapı */
export interface PlatformOrderSummary {
  id: string;
  orderNumber: string;
  customerName: string;
  totalAmount: number;
  createdAt: string;
  paymentStatus: 'PAID' | 'UNPAID' | 'CANCELLED' | 'UNKNOWN';
  invoiceNumber?: string;
  invoiceId?: string;
  /** Platform kaydındaki şube ID (Trendyol vb.) — şube seçili raporda şubesiz kayıtları elemek için */
  branchId?: string;
  platform: string;
  hasReceipt?: boolean;
  hasInvoice?: boolean;
}

/** Sağlama sonucu - her platform tab'ı için */
export interface ReconciliationResult {
  paid: PlatformOrderSummary[];
  unpaid: PlatformOrderSummary[];
  cancelled: PlatformOrderSummary[];
  warnings: PlatformOrderSummary[];
  paidTotal: number;
  unpaidTotal: number;
  cancelledTotal: number;
  warningTotal: number;
}
